//------------------------------------------------------ externos
import { useEffect, useState } from "react";
//------------------------------------------------------ elementos
import FormContent from "../funcionales/FormContent";
import MarcoModal from "../funcionales/MarcoModal";
import FormHeader from "../funcionales/FormHeader";
import TextButton from "../buttons/TextButton";
import Loading from "../../routes/Loading";
//------------------------------------------------------ funciones
import { cargarSelects } from "../../functions/dataFunctions";
import { submitPersona } from "./data/Submits";
import { elementos } from "./data/FormContent";
import { especializacionesOptions } from "./data/OptionsContent";
import {
  camposAltaPorteria,
  columnasDelVinculo,
  opcionesVinculo,
} from "../../shared/altaPorteria";
//import { refreshCache } from "../../services/cache";
//------------------------------------------------------ estilos
import { useCerrarConEsc } from "../../hooks/useCerrarConEsc";
import "./css/Forms.css";

const FormPersona = ({
  subtitle = "Persona",
  elemento = null,
  puesto = null,
  onGuardar,
  onClose,
  campoNoEditable = "null",
  /* La barrera con `cargar.fleteros` y sin `cargar.gente`: sólo un fletero o
   * un externo NUEVO (ver `shared/altaPorteria.js`). */
  soloFleterosYExternos = false,
}) => {
  useCerrarConEsc(onClose);

  const acotado = soloFleterosYExternos && !elemento;
  const modoEdicion = !!elemento;
  const titulo = modoEdicion ? "Editar" : "Agregar";

  const [loading, setLoading] = useState(true);
  const [listas, setListas] = useState({}); //para los selects, recibe un array tipo {tractores: ..., furgones: ...}
  const [formData, setFormData] = useState({
    // información personal
    id: elemento?.id || "",
    apellido: elemento?.apellido || "",
    nombres: elemento?.nombres || "",
    nacimiento: elemento?.nacimiento || "",
    ubicacion: elemento?.ubicacion || "",
    // información laboral
    legajo: elemento?.legajo || "",
    empresa: elemento?.empresa || "",
    telefono: elemento?.telefono || "",
    /* ARRANCA EN «EMPLEADO», que es lo que es casi todo el mundo: el campo
     * estaba cargado en 34 de 2.476 personas, o sea que preguntarlo en blanco
     * no lo hacía completar — lo hacía saltear. Se puede cambiar a proveedor o
     * externo cuando corresponde; lo que deja de pasar es que un alta normal
     * tenga que elegirlo. En modo edición manda lo que la fila ya trae, aunque
     * esté vacío: poner «EMPLEADO» sobre las 2.442 sin cargar sería inventar
     * un vínculo que nadie declaró. */
    tipo: modoEdicion ? elemento?.tipo || "" : acotado ? "" : "EMPLEADO",
    // Sólo el alta de la barrera: fletero o externo, sin nada elegido de antemano.
    ...(acotado ? { vinculo: "" } : {}),
    puesto: puesto || elemento?.puesto || "",
    especializacion: elemento?.especializacion || "",
    sucursal: elemento?.sucursal || "",
    ingreso: elemento?.ingreso || "",
    // Los seis de la planilla de Erick. Se preguntan acá y la nómina manda
    // cuando tiene fila para este DNI — ver el bloque de `elementos.personas`.
    cuil: elemento?.cuil || "",
    categoria: elemento?.categoria || "",
    calificacion: elemento?.calificacion || "",
    convenio: elemento?.convenio || "",
    contrato: elemento?.contrato || "",
    obra_social: elemento?.obra_social || "",
    // detalles y comentarios
    detalle: elemento?.detalle || "",
    comentario: elemento?.comentario || "",
    alerta: elemento?.alerta || "",
  });

  const especializaciones = especializacionesOptions(formData.puesto); // se carga dinamicamente porque espera el puesto

  /* ¿HAY ALGO ESCRITO? Es lo único que el marco necesita saber para no cerrar
   * de un clic perdido y llevarse media carga. En modo edición SIEMPRE hay
   * algo —la fila viene llena—, así que se compara contra lo que se abrió y no
   * contra el vacío: si no se tocó nada, cierra derecho. */
  const hayCambios = Object.keys(formData).some(
    (k) => String(formData[k] ?? "") !== String((elemento || {})[k] ?? ""),
  );

  /* EL OFICIO DEL TALLER SE CARGA EN EL TALLER, así que acá no se pregunta.
   *
   * Se mudó al padrón de mecánicos de la ticketera, que es de donde sale el
   * desplegable al asignar una reparación y donde el jefe de taller da de alta
   * y de baja. En los dos lados serían dos lugares para decir el mismo oficio,
   * y el desplegable se arma con uno solo: la mitad cargada del otro lado se
   * guardaría "bien" y no aparecería en ningún lado.
   *
   * SE SACA EL CAMPO ENTERO Y NO SÓLO SUS OPCIONES. Con la lista vacía el
   * `Select` no encuentra el valor cargado y se dibuja en blanco —o sea, las
   * 21 personas que hoy dicen MECANICO se verían sin especialización— y encima
   * es `isClearable`: un click de más lo borraba de verdad. Un dato que está y
   * se ve como que falta es peor que uno que no se puede editar.
   *
   * Y LO YA CARGADO VIAJA IGUAL: `formData` se inicializa desde la fila y se
   * envía entero, así que la especialización de quien la tenga se guarda tal
   * cual estaba. Esto deja de PREGUNTARLA, no la borra.
   */
  const campos = acotado
    ? camposAltaPorteria(elementos["personas"])
    : elementos["personas"].filter(
        (c) => !(c.key === "especializacion" && formData.puesto === "TALLER"),
      );

  useEffect(() => {
    const cargarDatos = async () => {
      const data = await cargarSelects(); // trae TODOS
      setListas(data);
    };

    cargarDatos();
    setLoading(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* En el alta de la barrera el vínculo elegido se traduce a las tres
     * columnas que la base exige (EXTERNO, y CHOFER + FLETERO si es fletero). */
    const datos = acotado
      ? { ...formData, ...(columnasDelVinculo(formData.vinculo) || {}) }
      : formData;

    await submitPersona(
      datos,
      campos,
      setLoading,
      onGuardar,
      onClose,
      modoEdicion,
      elemento?.id,
    );
  };

  return (
    <MarcoModal onClose={onClose} hayCambios={hayCambios}
                etiqueta={`${titulo} ${subtitle}`}>
      {loading ? (
        <Loading />
      ) : (
        <div className="form-content">
          <FormHeader title={titulo} subTitle={subtitle} onClose={onClose} />

          <FormContent
            elemento={elemento}
            campos={campos}
            opciones={{ ...listas, especializaciones,
                        ...(acotado ? { vinculosPorteria: opcionesVinculo() } : {}) }}
            data={formData}
            setData={setFormData}
            campoNoEditable={campoNoEditable}
          />

          <div className="form-buttons">
            <TextButton
              text={"Guardar"}
              type={"button"}
              onClick={handleSubmit}
            />
          </div>
        </div>
      )}
    </MarcoModal>
  );
};

export default FormPersona;
